// BuilderUploadPage.js
import React, { useMemo, useState } from "react";
import { api } from "../scripts/api";
import { UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";

function SmallPill({ children }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 12,
        border: "1px solid rgba(255,255,255,0.10)",
        background: "rgba(255,255,255,0.06)",
        lineHeight: 1.8,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function DiffCell({ d }) {
  if (!d) return "-";
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <div style={{ opacity: 0.8, fontSize: 12 }}>old: <b>{String(d.old ?? "") || "—"}</b></div>
      <div style={{ opacity: 0.95, fontSize: 12 }}>new: <b>{String(d.new ?? "") || "—"}</b></div>
    </div>
  );
}

export default function BuilderUploadPage() {
  const nav = useNavigate();
  const [table, setTable] = useState("all_inventory");
  const [file, setFile] = useState(null);

  const [loading, setLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [err, setErr] = useState("");

  const [preview, setPreview] = useState(null);

  const stats = preview?.stats || {};
  const changes = preview?.changes || [];

  const totalFieldsChanged = useMemo(() => {
    let n = 0;
    for (const c of changes) n += Object.keys(c.diff || {}).length;
    return n;
  }, [changes]);

  const doPreview = async () => {
    setErr("");
    setPreview(null);
    if (!file) return setErr("Pick an .xlsx file first.");

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("table", table);

      const res = await api.post("/import/openorders/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(res.data);
    } catch (e) {
      console.error("preview failed", e);
      setErr(e?.response?.data?.error || "Preview failed (check console/network).");
    } finally {
      setLoading(false);
    }
  };

  const doApply = async () => {
    setErr("");
    const hasChanges = !!preview?.changes?.length;
    const hasInserts = !!preview?.inserts?.length;
    if (!hasChanges && !hasInserts) return setErr("Nothing to apply.");

    setApplyLoading(true);
    try {
      const res = await api.post("/import/openorders/apply", {
        table,
        // UPDATEs for matched rows
        changes: (preview.changes || []).map((c) => ({
          id: c.record?.id,
          patch: Object.fromEntries(
            Object.entries(c.diff || {}).map(([k, v]) => [k, v.new])
          ),
        })),
        // INSERTs for unmatched rows — server marks them On Order
        // (color_code=8) so dispatch sees them immediately. The next-day
        // master-sheet sync fills in the rest of the columns.
        inserts: (preview.inserts || []).map((ins) => ({
          sheet:  ins.sheet,
          record: ins.record,
        })),
      });

      // re-preview so user sees a clean slate
      await doPreview();
      const d = res.data || {};
      alert(
        `Applied.\n` +
        `Updated rows: ${d.updatedRows ?? 0} (fields: ${d.updatedFields ?? 0})\n` +
        `Inserted (On Order): ${d.insertedRows ?? 0}` +
        (d.insertErrors?.length ? `\nInsert errors: ${d.insertErrors.length}` : "")
      );
    } catch (e) {
      console.error("apply failed", e);
      setErr(e?.response?.data?.error || "Apply failed (check console/network).");
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <div className="container space-y">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900 }}>Weekly Builder Upload</h2>
          <div className="muted" style={{ marginTop: 4 }}>
            Upload the builder spreadsheet → preview diffs → apply updates into SQLite.
          </div>
        </div>
        <button className="btn-outline" onClick={() => nav("/home")} title="Home">Home</button>
      </div>

      <div className="card">
        <div className="row" style={{ gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <SmallPill><FileSpreadsheet size={14} /> .xlsx only</SmallPill>

          <select className="input" style={{ width: 220 }} value={table} onChange={(e) => setTable(e.target.value)}>
            <option value="all_inventory">All Inventory</option>
            <option value="stock_inventory">Stock Inventory</option>
            <option value="used_inventory">Used Inventory</option>
            <option value="sold_inventory">Sold Inventory</option>
          </select>

          <input
            className="input"
            type="file"
            accept=".xlsx"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            style={{ width: 360 }}
          />

          <button className="btn" onClick={doPreview} disabled={loading}>
            <UploadCloud size={16} style={{ marginRight: 8 }} />
            {loading ? "Parsing…" : "Preview Changes"}
          </button>

          <button
            className="btn-outline"
            onClick={doApply}
            disabled={applyLoading || (!preview?.changes?.length && !preview?.inserts?.length)}
          >
            <CheckCircle2 size={16} style={{ marginRight: 8 }} />
            {applyLoading
              ? "Applying…"
              : `Apply${preview?.inserts?.length ? ` (+${preview.inserts.length} new)` : ""}`}
          </button>

          <button className="btn-outline" onClick={() => { setPreview(null); setErr(""); }} title="Reset">
            <RefreshCw size={16} style={{ marginRight: 8 }} /> Reset
          </button>
        </div>

        {err ? (
          <div className="row" style={{ marginTop: 10, gap: 8, alignItems: "center" }}>
            <AlertTriangle size={16} />
            <div className="muted">{err}</div>
          </div>
        ) : null}
      </div>

      {preview ? (
        <>
          <div className="scan-grid" style={{ marginTop: 10 }}>
            <div className="scan-tile" style={{ textAlign: "left" }}>
              <div className="scan-title">Sheet rows</div>
              <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4 }}>{stats.sheetRows ?? 0}</div>
              <div className="scan-sub">data rows parsed</div>
            </div>
            <div className="scan-tile" style={{ textAlign: "left" }}>
              <div className="scan-title">Matched records</div>
              <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4 }}>{stats.matched ?? 0}</div>
              <div className="scan-sub">found in DB</div>
            </div>
            <div className="scan-tile" style={{ textAlign: "left" }}>
              <div className="scan-title">Unmatched → Insert</div>
              <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4, color: "#16a34a" }}>{stats.willInsert ?? preview.inserts?.length ?? 0}</div>
              <div className="scan-sub">new "On Order" rows</div>
            </div>
            <div className="scan-tile" style={{ textAlign: "left" }}>
              <div className="scan-title">Rows w/ changes</div>
              <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4 }}>{changes.length}</div>
              <div className="scan-sub">fields changed: {totalFieldsChanged}</div>
            </div>
          </div>

          <div className="card" style={{ overflowX: "auto" }}>
            <div style={{ fontWeight: 900, marginBottom: 10 }}>Change Preview</div>

            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {["DB ID", "Match Key", "PROD ORDER #", "CUSTOMER PO", "VIN", "Diffs"].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: 10, borderBottom: "1px solid rgba(255,255,255,0.10)", opacity: 0.8 }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {changes.map((c, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                    <td style={{ padding: 10, whiteSpace: "nowrap" }}>#{c.record?.id ?? "?"}</td>
                    <td style={{ padding: 10, whiteSpace: "nowrap" }}>{c.match?.type ?? "-"}</td>
                    <td style={{ padding: 10, whiteSpace: "nowrap" }}>{c.sheet?.prod_order ?? "-"}</td>
                    <td style={{ padding: 10, whiteSpace: "nowrap" }}>{c.sheet?.customer_po ?? "-"}</td>
                    <td style={{ padding: 10, whiteSpace: "nowrap" }}>{c.sheet?.vin ?? "-"}</td>
                    <td style={{ padding: 10 }}>
                      <div style={{ display: "grid", gap: 10 }}>
                        {Object.entries(c.diff || {}).map(([field, d]) => (
                          <div key={field} style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 12 }}>
                            <div style={{ fontWeight: 800 }}>{field}</div>
                            <DiffCell d={d} />
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
                {!changes.length ? (
                  <tr>
                    <td colSpan={6} className="muted" style={{ padding: 12 }}>
                      No changes detected.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {preview.inserts?.length ? (
            <div className="card" style={{ overflowX: "auto" }}>
              <div style={{ fontWeight: 900, marginBottom: 4 }}>
                New "On Order" rows ({preview.inserts.length})
              </div>
              <div className="muted" style={{ marginBottom: 10 }}>
                These sheet rows didn't match any existing inventory record. Apply will INSERT them
                with <code>color_code = 8</code> (On Order) so dispatch sees them right away — the
                next master-sheet sync will fill in the remaining fields.
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    {["PROD ORDER #", "CUSTOMER PO", "VIN", "Pre-fill"].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: 10, borderBottom: "1px solid rgba(255,255,255,0.10)", opacity: 0.8 }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.inserts.map((ins, idx) => {
                    // Strip the housekeeping columns (color_code, unit_status,
                    // and the three identifier columns we display separately)
                    // so the "Pre-fill" cell only shows the substantive
                    // fields the import is seeding.
                    const r = ins?.record || {};
                    const skip = new Set([
                      "color_code", "unit_status",
                      preview.matchCols?.dbProdOrderCol,
                      preview.matchCols?.dbCustomerPoCol,
                      preview.matchCols?.vinDbCol,
                    ]);
                    const extras = Object.entries(r).filter(([k]) => k && !skip.has(k));
                    return (
                      <tr key={idx} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                        <td style={{ padding: 10, whiteSpace: "nowrap" }}>{ins.sheet?.prod_order || "-"}</td>
                        <td style={{ padding: 10, whiteSpace: "nowrap" }}>{ins.sheet?.customer_po || "-"}</td>
                        <td style={{ padding: 10, whiteSpace: "nowrap" }}>{ins.sheet?.vin || "-"}</td>
                        <td style={{ padding: 10, fontSize: 12 }}>
                          {extras.length ? (
                            <div style={{ display: "grid", gap: 4 }}>
                              {extras.map(([k, v]) => (
                                <div key={k}>
                                  <span style={{ opacity: 0.7 }}>{k}:</span>{" "}
                                  <span style={{ fontWeight: 700 }}>{String(v)}</span>
                                </div>
                              ))}
                            </div>
                          ) : <span className="muted">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}

          {preview.unmatched?.length && !preview.inserts?.length ? (
            <div className="card">
              <div style={{ fontWeight: 900, marginBottom: 10 }}>Unmatched rows (first 50)</div>
              <div className="muted" style={{ marginBottom: 10 }}>
                These rows didn't match any inventory record by PROD ORDER # / CUSTOMER PO / VIN.
              </div>
              <pre style={{ whiteSpace: "pre-wrap", margin: 0, opacity: 0.9 }}>
                {JSON.stringify(preview.unmatched.slice(0, 50), null, 2)}
              </pre>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
